import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import { environment } from '../../../../environments/environment'
import { ParametreDto } from './dto/parametre-dto'
import { ParametreTableauDto } from './dto/parametre-tableau-dto'
import { ParametreTableauSaveDto } from './dto/parametre-tableau-save-dto'
import { ParametreSpecfiqueBddEnedisDto } from './dto/parametre-specfique-bdd-enedis-dto'
import { SunleonService } from 'src/app/sunleon/data-access/http/sunleon.service'

@Injectable({
  providedIn: 'root',
})
export class ParametreService {
  public readonly familleParDefaut = 'bilaneco'

  private parametresSubject = new BehaviorSubject<ParametreDto[]>([])
  public parametres$ = this.parametresSubject.asObservable()
  private initialized = false

  constructor(
    private http: HttpClient,
    private sunleonService: SunleonService
  ) {
    this.setupOverloadableSubscriptions();
  }

  private setupOverloadableSubscriptions(): void {
    this.sunleonService.overloadableParametres$.subscribe(({ code, value }) => {
      this.updateParametreValue(code, value);
    });

    this.sunleonService.overloadableParametresTableau$.subscribe(({ parametreDto, valeurs }) => {
      this.updateParametreTableau(parametreDto, valeurs);
    });

    this.sunleonService.overloadableParametresSpécifique$.subscribe(({ parametreDto, valeurs }) => {
      this.updateParametreSpecifique(parametreDto, valeurs);
    });
  }

  private updateParametreValue(code: string, value: string): void {
    const current = this.parametresSubject.value;
    const updated = current.map(p => 
      p.code === code ? { ...p, valeur: value } : p
    );
    this.parametresSubject.next(updated);
  }

  private updateParametreTableau(parametreDto: ParametreDto, valeurs: ParametreTableauSaveDto): void {
    const current = this.parametresSubject.value;
    const updated = current.map(p => {
      if (p.code === parametreDto.code) {

        const newValeur = parametreDto.valeur || `Tableau (${(valeurs as any).datas?.length || 0} lignes)`;
        return { ...p, valeur: newValeur };
      }
      return p;
    });
    this.parametresSubject.next(updated);
  }

  private updateParametreSpecifique(parametreDto: ParametreDto, valeurs: ParametreSpecfiqueBddEnedisDto): void {
    const current = this.parametresSubject.value;
    const updated = current.map(p => {
      if (p.code === parametreDto.code) {

        const newValeur = parametreDto.valeur || `Spécifique (${valeurs.lignes?.length || 0} lignes)`;
        return { 
          ...p, 
          valeur: newValeur
        };
      }
      return p;
    });
    this.parametresSubject.next(updated);
  }

  getParametreByCode(code: string): Observable<ParametreDto> {
    const path = `${environment.baseUrl}/parametres/${code}`;
    return this.http.get<ParametreDto>(path);
  }

  getParametresByFamille(famille: string): Observable<ParametreDto[]> {
    const path = `${environment.baseUrl}/parametres/famille/${famille}`;
    return this.http.get<ParametreDto[]>(path);
  }

  getOverloadableParametres(): Observable<ParametreDto[]> {
    if (!this.initialized) {
      this.initialized = true;
      const path = `${environment.baseUrl}/parametres/overloadable`;
      this.http.get<ParametreDto[]>(path).subscribe({
        next: (dtos) => {
          this.parametresSubject.next(dtos);
        },
        error: (err) => {
          console.error('Error fetching initial overloadable parameters:', err);
          this.parametresSubject.next([]);
        }
      });
    }
    return this.parametres$;
  }

  getParametresTableau(codeParametre: string): Observable<ParametreTableauDto> {
    const path = `${environment.baseUrl}/parametres/tableau/${codeParametre}`
    return this.http.get<ParametreTableauDto>(path)
  }

  saveParametreTypeSimple(parametreDto: ParametreDto, valeur: string) {
    parametreDto.valeur = valeur
    const path = `${environment.baseUrl}/parametres/` + parametreDto.code
    return this.http.put(path, parametreDto)
  }

  saveParametreTypeTableau(parametreDto: ParametreDto, valeurs: ParametreTableauSaveDto) {
    const path = `${environment.baseUrl}/parametres/tableau/` + parametreDto.code
    return this.http.put(path, valeurs)
  }

  getParametresBddEnedis(codeParametre: string): Observable<ParametreSpecfiqueBddEnedisDto> {
    const path = `${environment.baseUrl}/parametres/specifique/bddenedis/${codeParametre}`
    return this.http.get<ParametreSpecfiqueBddEnedisDto>(path)
  }

  saveParametreBddEnedis(parametreDto: ParametreDto, valeurs: ParametreSpecfiqueBddEnedisDto) {
    const path = `${environment.baseUrl}/parametres/specifique/` + parametreDto.code
    return this.http.put(path, valeurs)
  }

  refreshOverloadableParametres(): void {
    this.initialized = false; 
    this.getOverloadableParametres().subscribe();
  }
}